import {
  Avatar,
  AvatarFallback,
  AvatarImage,
} from "@punchup/ui-common/shadcn/ui/avatar";
import { cn } from "@punchup/utils/cn";

import { getComedianInitials } from "@punchup/utils/getComedianInitials";

import { AvatarShimmer } from "@punchup/ui-common/components/common";
import { comedianGradientBg } from "@punchup/ui-common/resources";

interface ComedianAvatarProps {
  comedianImgUrl?: string;
  comedianName: string;
  isNotPunchupComedian?: boolean;
  isFollowed?: boolean;
  size?: "xs" | "sm" | "md" | "lg";
  className?: string;
}

export function ComedianAvatar({
  comedianImgUrl,
  comedianName,
  isNotPunchupComedian = false,
  isFollowed = false,
  size = "sm",
  className,
}: ComedianAvatarProps) {
  const isPunchupComedian = !isNotPunchupComedian;

  const sizeClass =
    size === "xs"
      ? "w-8 h-8"
      : size === "sm"
        ? "w-10 h-10"
        : size === "md"
          ? "h-10 w-10 md:w-16 md:h-16"
          : "h-20 w-20 md:w-24 md:h-24";

  // Build avatar container class
  const avatarContainerClass = cn(
    sizeClass,
    isPunchupComedian &&
      isFollowed &&
      "ring-[3px] ring-primary ring-offset-0 ring-offset-background",
    isPunchupComedian &&
      !isFollowed &&
      "group-hover:ring-[3px] group-hover:ring-primary group-hover:ring-offset-0 group-hover:ring-offset-background relative cursor-pointer",
    "shadow-md hover:shadow-lg transition-shadow duration-200",
    className
  );

  // Render different avatar content based on conditions
  const renderAvatarContent = () => {
    // Not a Punchup comedian
    if (isNotPunchupComedian) {
      // Has an image
      if (comedianImgUrl) {
        return (
          <AvatarImage
            src={comedianImgUrl}
            className="object-cover object-center block"
            alt={comedianName}
          />
        );
      }
      // No image, show initials
      return (
        <AvatarFallback className="bg-light-orange text-primary">
          {getComedianInitials(comedianName)}
        </AvatarFallback>
      );
    }

    // Is a Punchup comedian
    return (
      <>
        <AvatarImage
          src={comedianImgUrl}
          alt={comedianName}
          className="object-cover object-center block"
        />
        <AvatarFallback>
          <AvatarShimmer />
        </AvatarFallback>
      </>
    );
  };

  return (
    <Avatar
      className={avatarContainerClass}
      style={{
        backgroundImage: `url(${comedianGradientBg.src})`,
        backgroundSize: "cover",
        backgroundPosition: "center",
      }}
    >
      {renderAvatarContent()}
    </Avatar>
  );
}
